import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, PackageCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useMarkPlanningLotReceived } from "@/hooks/usePlanningLots";
import { useVolumeUnit } from "@/hooks/useOrganization";
import type { VesselAssignmentWithRelations } from "@/hooks/useVesselAssignments";

interface MarkReceivedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignment: VesselAssignmentWithRelations | null;
}

export function MarkReceivedDialog({
  open,
  onOpenChange,
  assignment,
}: MarkReceivedDialogProps) {
  const planningLot = assignment?.planning_lot;
  const [volume, setVolume] = useState("");

  const { volumeLabel } = useVolumeUnit();
  const markReceived = useMarkPlanningLotReceived();
  const queryClient = useQueryClient();

  // Reinitialize volume whenever the dialog opens for a (possibly different) lot
  useEffect(() => {
    if (open) {
      setVolume(planningLot?.current_volume_l?.toString() ?? "");
    }
  }, [open, planningLot?.id]);

  const handleSubmit = async () => {
    if (!planningLot) return;
    const parsedVolume = volume !== "" ? parseFloat(volume) : null;

    await markReceived.mutateAsync({
      id: planningLot.id,
      input: {
        current_volume_l: parsedVolume,
      },
    });

    // Bars/popover render vessel_assignment.fill_volume_l, not planning_lot.current_volume_l —
    // keep the two in sync or the confirmed volume won't show up on the bar.
    if (assignment && parsedVolume !== assignment.fill_volume_l) {
      const { error } = await supabase
        .from("vessel_assignment")
        .update({ fill_volume_l: parsedVolume })
        .eq("id", assignment.id);
      if (!error) {
        queryClient.invalidateQueries({ queryKey: ["vessel-assignments"] });
        queryClient.invalidateQueries({ queryKey: ["on-deck-assignments"] });
      }
    }

    onOpenChange(false);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  if (!planningLot) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="h-4 w-4 text-emerald-500" />
            Mark as Received
          </DialogTitle>
          <DialogDescription>
            Confirm receipt of{" "}
            <span className="font-mono font-semibold text-foreground">
              {planningLot.code}
            </span>
            . Update the volume if the actual amount differs from the estimate.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Lot summary */}
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm space-y-0.5">
            {planningLot.client && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Client</span>
                <span>{planningLot.client.name}</span>
              </div>
            )}
            {planningLot.varietal && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Varietal</span>
                <span>{planningLot.varietal}</span>
              </div>
            )}
            {planningLot.vintage && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Vintage</span>
                <span>{planningLot.vintage}</span>
              </div>
            )}
          </div>

          {/* Volume correction */}
          <div>
            <Label htmlFor="received-volume">
              Actual Volume ({volumeLabel})
            </Label>
            <Input
              id="received-volume"
              type="number"
              value={volume}
              onChange={e => setVolume(e.target.value)}
              placeholder="Enter received volume"
              min="0"
              step="10"
              className="mt-1"
              autoFocus
            />
            {planningLot.current_volume_l != null && (
              <p className="text-xs text-muted-foreground mt-1">
                Estimated: {planningLot.current_volume_l.toLocaleString()} {volumeLabel}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={markReceived.isPending}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {markReceived.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
              ) : (
                "Confirm Receipt"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
